namespace $.$$ {
	export class $shm_hitalama_map_widget_page extends $.$shm_hitalama_map_widget_page {

		tab( next?: string ) {
			return this.Map_page().widget_props_tab( next )
		}

		body() {
			return this.tab() === 'text' ? [ this.Text() ] : [ ...this.general_body(), ...this.general_tail() ]
		}

		Map_page(): $.$shm_hitalama_map_page {
			const map_id = this.widget().map().link().str
			return this.$.$shm_hitalama_app.Root(0).Maps().Book( map_id ).Map_page()
		}

		width( next?: number ): number {
			return this.$.$mol_state_local.value( '$' + 'hitalama_map_widget_page.width', next ) ?? 290
		}
		
		Widget() {
			return this.Map_page().Widget( this.widget().link() ).Sub()
		}
		
		title( next?: string ) {
			return this.widget().title( next ) ?? ''
		}

		widget_type_title() {
			const type = this.widget().type()
			return $shm_hitalama_map_widget_def( type )?.title ?? type
		}

		@ $mol_mem
		Widget_type_icon(): $mol_icon_square_outline {
			const Icon = $shm_hitalama_map_widget_def( this.widget().type() )?.icon
			if( !Icon ) return super.Widget_type_icon()
			return new Icon() as $mol_icon_square_outline
		}
		
		markdown_images_enabled() { return this.widget().type() === 'text_md' && this.widget().can_change() }

		text( next?: string ): string {
			return this.widget().text( next ) ?? ''
		}

		@ $mol_action
		text_focus() {
			this.Text().Edit().focused( true )
			const editor = this.Text().Edit().dom_node_actual() as HTMLTextAreaElement
			editor.setSelectionRange( editor.value.length, editor.value.length )
		}

		history_edit_start( event?: FocusEvent | null ) {
			// Parameters live beside the map pane, outside its focus/edit handlers.
			return this.Map_page().history_edit_start( event )
		}

		history_edit_end( event?: FocusEvent | null ) {
			return this.Map_page().history_edit_end( event )
		}

		text_visible() {
			return [ 'file', 'image' ].includes( this.widget().type() ) ? [] : super.text_visible()
		}

		external_link( next?: string ): string {
			return this.widget().link_url( next )
		}

		data_keys() {
			return Object.keys( this.data_raw() )
		}

		data_rows() {
			return this.data_keys().map( key => this.Data_labeler( key ) )
		}

		data_key( key: string ) {
			return key
		}

		@ $mol_mem_key
		data_value_draft( key: string, next?: string | null ) {
			return next ?? null
		}

		data_value_text( key: string, next?: string ): string {
			if( next !== undefined ) {
				const parsed = this.$.$mol_error_fence(
					()=> JSON.parse( next ),
					()=> undefined,
				)

				if( parsed !== undefined ) {
					this.widget().Data( null )?.val({
						... this.data_raw(),
						[ key ]: parsed,
					})
					this.data_value_draft( key, null )
				} else {
					this.data_value_draft( key, next )
				}

				return next
			}

			const draft = this.data_value_draft( key )
			if( draft !== null ) return draft

			const data = this.data_raw()
			return JSON.stringify( data[ key ], null, '\t' ) ?? 'null'
		}

		data_raw(): Record< string, any > {
			return this.widget().Data()?.val() ?? {}
		}
		
		x( next?: number ): number {
			return this.Widget().left( next )
		}
		
		y( next?: number ): number {
			return this.Widget().top( next )
		}
		
		widget_width( next?: number ): number {
			return this.Widget().width( next )
		}

		height( next?: number ): number {
			return this.Widget().height( next )
		}

		auto_height( next?: boolean ): boolean {
			return this.widget().auto_height( next )
		}

		z( next?: number ): number {
			return this.Widget().z( next )
		}

		type_settings_visible() {
			return this.type_settings().length > 0 ? super.type_settings_visible() : []
		}

		type_settings() {
			return this.Widget().settings_content()
		}

		hide_head_tools( next?: boolean ) {
			return this.widget().hide_head_tools( next )
		}

		background_color( next?: string ) {
			return this.widget().background_color(
				next === undefined ? undefined : this.color_normalize( next )
			)
		}

		background_color_default() {
			return this.widget().background_color_default()
		}

		background_opacity( next?: number ) {
			if( next !== undefined ) next = Math.max( 0, Math.min( 1, next ) )
			return this.widget().background_opacity( next )
		}

		background_opacity_percent( next?: number ) {
			if( next !== undefined ) {
				const opacity = Number.isFinite( next ) ? next / 100 : 1
				this.background_opacity( opacity )
				return next
			}

			return Math.round( this.background_opacity() * 100 )
		}

		background_opacity_percent_selected( next?: string ): string {
			if( next ) this.background_opacity_percent( Number( next ) )
			return next ?? ''
		}

		color_normalize( color: string ) {
			const value = color.trim()
			const short = value.match( /^#([\da-f])([\da-f])([\da-f])$/i )
			if( short ) return `#${ short[1] }${ short[1] }${ short[2] }${ short[2] }${ short[3] }${ short[3] }`.toLowerCase()

			const hex = value.match( /^#?([\da-f]{6})$/i )
			if( hex ) return `#${ hex[1] }`.toLowerCase()

			const rgb = value.match( /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i )
			if( !rgb ) return value

			const to_hex = ( next: string )=> Math.max( 0, Math.min( 255, Number( next ) ) )
				.toString( 16 )
				.padStart( 2, '0' )
			return `#${ to_hex( rgb[1] ) }${ to_hex( rgb[2] ) }${ to_hex( rgb[3] ) }`
		}

		border_color( next?: string ) {
			return this.widget().border_color(
				next === undefined ? undefined : this.color_normalize( next )
			)
		}

		border_opacity( next?: number ) {
			if( next !== undefined ) next = Math.max( 0, Math.min( 1, next ) )
			return this.widget().border_opacity( next )
		}

		border_opacity_percent( next?: number ) {
			if( next !== undefined ) {
				const opacity = Number.isFinite( next ) ? next / 100 : 1
				this.border_opacity( opacity )
				return next
			}

			return Math.round( this.border_opacity() * 100 )
		}

		border_opacity_percent_selected( next?: string ): string {
			if( next ) this.border_opacity_percent( Number( next ) )
			return next ?? ''
		}

		opacity_percent_suggests(): readonly string[] {
			return this.opacity_percent_options().map( opacity => opacity.toString() )
		}

		border_width( next?: number ) {
			if( next !== undefined ) next = Math.max( 0, next )
			return this.widget().border_width( next )
		}

		border_style( next?: string ) {
			return this.widget().border_style( next )
		}

		border_style_option( id: any ) {
			return id
		}

		border_style_option_content( id: any ) {
			if( id === 'none' ) return [ this.Border_style_none() ]
			return [ this.Border_style_option( id ) ]
		}

		border_style_content() {
			if( this.border_style() === 'none' ) return [ this.Border_style_current_none() ]
			return [ this.Border_style_current() ]
		}

		color( next?: string ) {
			if( next === undefined ) return this.widget().color() || 'var(--mol_theme_text)'

			this.widget().map().Last_color(null)?.val(next)
			return this.widget().color(next)
		}

		font_size( next?: number ) {
			if( next !== undefined ) this.widget().map().Last_font_size(null)?.val(next)
			return this.widget().font_size(next)
		}

		font_family( next?: string ) {
			return this.widget().font_family(next)
		}
		
	}
}
